import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { useHistory } from 'react-router-dom';

// COMPONENTS
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import LoadingSpinner from '../../components/LoadingSpinner';
import Button from '../../components/Button';

// ICONS
import { ReactComponent as ArrowLeftIcon } from '../../assets/svg/chevron-left.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/svg/chevron-right.svg';
import { ReactComponent as ArrowLeft } from '../../assets/svg/arrow-left.svg';

// TYPES
import { ComplaintMemberCandidatesMembers, ComplaintCreateCommand } from '../../api/models';
import { FileType } from '../../components/FileUpload';

export interface ComplaintsFormDataTypes extends ComplaintCreateCommand {
	assignees?: string[];
	attachments?: FileType[];
}

const ComplaintsSubmit: React.FC = () => {
	const history = useHistory();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Set up form state and initial form data
	const initialComplaintsFormData: ComplaintsFormDataTypes = {
		loggingType: undefined,
		loggingOnBehalf: undefined,
		anonymousContact: undefined,
		category: undefined,
		subject: undefined,
		occurrence: undefined,
		authority: undefined,
		location: undefined,
		accused: undefined,
		accusedRelationship: undefined,
		impacted: undefined,
		impactedRelationship: undefined,
		othersInvolved: undefined,
		witnesses: undefined,
		isInitialComplaint: undefined,
		description: undefined,
		comments: undefined,
		assignees: [],
		excludedLeaders: [],
		familyMembers: [],
		createAttachments: [],
		attachments: []
	};

	const [complaintsFormData, setComplaintsFormData] = React.useState(initialComplaintsFormData);

	const initialAssigneesData = {
		minimumLeaders: 1,
		leaders: [],
		familyMembers: []
	};

	const [assigneesData, setAssigneesData] = React.useState<ComplaintMemberCandidatesMembers>(initialAssigneesData);

	const getComplaintsNotifiedMembersList = async () => {
		setIsLoading(true);

		const response = await apibridge.getComplaintsNotifiedMembersList();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialAssigneesData } = response;

			setIsLoading(false);

			setAssigneesData(result);
		}
	};

	React.useEffect(() => {
		getComplaintsNotifiedMembersList();
	}, []);

	const { minimumLeaders, leaders, familyMembers: familyMembersData } = assigneesData;

	// State to control form steps
	const [activeStep, setActiveStep] = React.useState('step1');

	// Logic for Back Button
	const mobileStepHandler = () => {
		if (activeStep === 'step3') {
			setActiveStep('step2');
		}

		if (activeStep === 'step2') {
			setActiveStep('step1');
		}

		if (activeStep === 'step1') {
			history.push('/complaints');
		}
	};

	React.useEffect(() => {
		const pageState = history.location.state;

		if (pageState === undefined || pageState.activeStep === undefined || pageState.activeStep === null) {
			history.replace(window.location.pathname, { activeStep });
		} else if (pageState.activeStep !== activeStep) {
			history.push(window.location.pathname, { activeStep });
		}

		let unlisten = history.listen((location, action) => {
			if (
				action === 'POP' &&
				location.state &&
				location.state.activeStep !== undefined &&
				location.state.activeStep !== null
			) {
				setActiveStep(location.state.activeStep);
			}
		});

		return unlisten;
	}, [activeStep]);

	const goBack = () => {
		mobileStepHandler();
	};

	return (
		<div className="page-general-layout">
			<div className="page-heading">
				<Button className="btn-text-icon" onClick={goBack} buttonIcon={<ArrowLeft className="link-icon" />}>
					Back
				</Button>

				<h1 className="h1">Submit a complaint</h1>
			</div>

			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="form-steps-controller-desktop d-none d-lg-flex">
									<div
										className={`steps-name ${
											activeStep === 'step1' || activeStep === 'step2' || activeStep === 'step3' ? 'active' : ''
										}`}
										onClick={() => {
											setActiveStep('step1');
										}}
									>
										1. Complaint details
									</div>

									<ArrowRightIcon className="step-icon" />

									<div
										className={`steps-name ${activeStep === 'step2' || activeStep === 'step3' ? 'active' : ''}`}
										onClick={() => {
											setActiveStep('step2');
										}}
									>
										2. Who to notify
									</div>

									<ArrowRightIcon className="step-icon" />

									<div className={`steps-name ${activeStep === 'step3' ? 'active' : ''}`}>3. Review & Submit</div>
								</div>

								<div className="form-steps-controller-mobile d-flex d-lg-none">
									<div className="steps-name-group">
										{activeStep === 'step1' && <div className="steps-name active">1. Complaint details</div>}

										{activeStep === 'step2' && <div className="steps-name active">2. Who to notify</div>}

										{activeStep === 'step3' && <div className="steps-name active">3. Review & Submit</div>}
									</div>

									<div className="controller-group">
										<div
											className={`controller-wrapper ${
												activeStep === 'step2' || activeStep === 'step3' ? 'active' : ''
											}`}
											onClick={mobileStepHandler}
										>
											<ArrowLeftIcon className="step-controller" />
										</div>

										<div className="controller-wrapper">
											<ArrowRightIcon className="step-controller" />
										</div>
									</div>
								</div>

								<div className="general-divider"></div>

								{activeStep === 'step1' && (
									<StepOne
										complaintsFormData={complaintsFormData}
										setComplaintsFormData={setComplaintsFormData}
										setActiveStep={setActiveStep}
									/>
								)}

								{activeStep === 'step2' && (
									<StepTwo
										complaintsFormData={complaintsFormData}
										setComplaintsFormData={setComplaintsFormData}
										setActiveStep={setActiveStep}
										minimumLeaders={minimumLeaders || 4}
										leaders={leaders || []}
										familyMembersData={familyMembersData || []}
									/>
								)}

								{activeStep === 'step3' && (
									<StepThree
										complaintsFormData={complaintsFormData}
										setComplaintsFormData={setComplaintsFormData}
										setActiveStep={setActiveStep}
										leaders={leaders || []}
										familyMembersData={familyMembersData || []}
									/>
								)}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default ComplaintsSubmit;
